import { Button, type ButtonProps, LuIcon } from "@overmap-ai/blocks"
import { memo, useCallback, useMemo } from "react"

import { modelIconMapping } from "../../mappings/icons"
import {
	setAllActiveProcedureInitiatives,
	setAllAssetTypeMapFilters,
	setAllIssueTypeMapFilters,
	setAllProcedureInitiativeMapFilters,
	setGeoImageDateRange,
	setHiddenAssetTypeIds,
	setHiddenIssueTypeIds,
	useAppDispatch,
	useAppSelector,
} from "../../store"

type ClearMapFilterButtonProps = Omit<ButtonProps, "onClick">

export const ClearMapFilterButton = memo((props: ClearMapFilterButtonProps) => {
	const dispatch = useAppDispatch()

	//
	const { hiddenAssetTypeIds, hiddenIssueTypeIds, geoImageDateRange } = useAppSelector((state) => state.mapVisibility)
	const { assetTypeFilters, issueTypeFilters, procedureInitiativeFilters, activeProcedureInitiatives } =
		useAppSelector((state) => state.mapFilters)

	const hasActiveFilters = useMemo(() => {
		// hidden
		const hasHiddenAssetTypes = Object.keys(hiddenAssetTypeIds).length > 0
		const hasHiddenIssueTypes = Object.keys(hiddenIssueTypeIds).length > 0

		// filters
		const hasAssetTypeFilters = Object.values(assetTypeFilters).some((filters) => filters.length > 0)
		const hasIssueTypeFilters = Object.values(issueTypeFilters).some((filters) => filters.length > 0)
		const hasProcedureInitiativeProcedures =
			Object.values(procedureInitiativeFilters).some((filters) => filters.length > 0) ||
			Object.values(activeProcedureInitiatives).some((procedureInitiativeId) => !!procedureInitiativeId)

		const hasGeoImageDateRange = geoImageDateRange !== null

		return (
			hasHiddenAssetTypes ||
			hasHiddenIssueTypes ||
			hasGeoImageDateRange ||
			hasAssetTypeFilters ||
			hasIssueTypeFilters ||
			hasProcedureInitiativeProcedures
		)
	}, [
		hiddenAssetTypeIds,
		hiddenIssueTypeIds,
		assetTypeFilters,
		issueTypeFilters,
		procedureInitiativeFilters,
		activeProcedureInitiatives,
		geoImageDateRange,
	])

	const handleClearFilters = useCallback(() => {
		// Clear MapVisibility filters
		dispatch(setHiddenAssetTypeIds([]))
		dispatch(setHiddenIssueTypeIds([]))

		// filters
		dispatch(setAllIssueTypeMapFilters({}))
		dispatch(setAllAssetTypeMapFilters({}))
		dispatch(setAllProcedureInitiativeMapFilters({}))
		dispatch(setAllActiveProcedureInitiatives({}))
		dispatch(setGeoImageDateRange(null))
	}, [dispatch])

	if (!hasActiveFilters) return null

	return (
		<Button onClick={handleClearFilters} {...props}>
			<LuIcon icon={modelIconMapping.close} />
			Clear
		</Button>
	)
})

ClearMapFilterButton.displayName = "ClearMapFilterButton"
